import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { subMonths, startOfMonth, endOfMonth, format, addDays } from "date-fns";

export interface MonthlyRevenue {
  month: string;
  revenue: number;
}

export interface ExpiringMembership {
  userId: string;
  userName: string;
  expiresAt: Date;
  daysLeft: number;
}

export interface GymFinancials {
  revenueByMonth: MonthlyRevenue[];
  expiringMemberships: ExpiringMembership[];
  averageTicket: number;
}

export const useGymFinancials = (gymId: string) => {
  return useQuery({
    queryKey: ["gym-financials", gymId],
    queryFn: async (): Promise<GymFinancials> => {
      const now = new Date();

      // Get users from this gym
      const { data: users } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, username")
        .eq("gym_id", gymId);

      const userIds = users?.map(u => u.id) || [];

      // Get revenue for last 6 months
      const revenueByMonth: MonthlyRevenue[] = [];
      for (let i = 5; i >= 0; i--) {
        const monthStart = startOfMonth(subMonths(now, i));
        const monthEnd = endOfMonth(subMonths(now, i));

        const { data: payments } = await supabase
          .from("payments")
          .select("amount")
          .in("user_id", userIds.length > 0 ? userIds : ['no-users'])
          .gte("paid_at", monthStart.toISOString())
          .lte("paid_at", monthEnd.toISOString());

        const revenue = payments?.reduce((acc, p) => acc + Number(p.amount), 0) || 0;
        revenueByMonth.push({
          month: format(monthStart, "MMM"),
          revenue,
        });
      }

      // Get expiring memberships (payments expiring in next 30 days)
      const { data: recentPayments } = await supabase
        .from("payments")
        .select("user_id, paid_at, validity_days, profiles(first_name, last_name, username)")
        .in("user_id", userIds.length > 0 ? userIds : ['no-users'])
        .order("paid_at", { ascending: false });

      // Group by user and get their last payment
      const lastPaymentByUser = new Map<string, { paid_at: string; validity_days: number; profile: any }>();
      recentPayments?.forEach(p => {
        if (!lastPaymentByUser.has(p.user_id)) {
          lastPaymentByUser.set(p.user_id, {
            paid_at: p.paid_at || '',
            validity_days: p.validity_days,
            profile: p.profiles,
          });
        }
      });

      const expiringMemberships: ExpiringMembership[] = [];
      lastPaymentByUser.forEach((payment, userId) => {
        if (payment.paid_at) {
          const expiresAt = addDays(new Date(payment.paid_at), payment.validity_days);
          const daysLeft = Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
          
          if (daysLeft > 0 && daysLeft <= 30) {
            const profile = payment.profile;
            const userName = profile?.first_name && profile?.last_name
              ? `${profile.first_name} ${profile.last_name}`
              : profile?.username || 'Usuario';

            expiringMemberships.push({
              userId,
              userName,
              expiresAt,
              daysLeft,
            });
          }
        }
      });

      // Sort by days left (ascending)
      expiringMemberships.sort((a, b) => a.daysLeft - b.daysLeft);

      // Calculate average ticket
      const { data: allPayments } = await supabase
        .from("payments")
        .select("amount")
        .in("user_id", userIds.length > 0 ? userIds : ['no-users']);

      const totalAmount = allPayments?.reduce((acc, p) => acc + Number(p.amount), 0) || 0;
      const averageTicket = allPayments?.length ? totalAmount / allPayments.length : 0;

      return {
        revenueByMonth,
        expiringMemberships: expiringMemberships.slice(0, 10), // Top 10
        averageTicket,
      };
    },
    enabled: !!gymId,
  });
};
